import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { LazyQueryExecFunction } from '@apollo/client';
import styles from './EditLessonForm.module.scss';
import { TextInput, TextInputValue } from '@components/text-input/TextInput';
import Select from 'src/components/select/Select';
import RadioGroup from "src/components/radio-group/RadioGroup";
import ImageUploadForm from '../../../unit/edit/edit-content/image-form/ImageUploadForm';
import TagsForm from '../../../unit/edit/edit-content/tags-form/TagsForm';
import AnswersForm from '../edit-content/feedback-form-editor/form-component/form-answers/AnswersForm';
import { GetUrlQuery, Exact, VideoUploadInput } from '@api/graphql-codegen';
import { useQueryDictionary } from '@hooks/useQueryDictionary';
import { EditSllLesson, SelectOption } from '@types';
import {
  gradeLabelPositionOptions,
  standaloneLessonsSelCompetencies,
  categoryMoodOptions1,
  categorySkillsOptions,
  categoryDirectionOptions,
  categoryTargetAreaOptions,
  categoryTiersOptions,
  themeOptions,
  AustraliaStandaloneLessonAgeGroupOptions,
  NorthAmericaStandaloneLessonAgeGroupOptions,
  lessonTypeOptionsWithNone,
} from '@constants';
import SelectMulti from '@src/components/select-multi/SelectMulti';
import Checkbox from '@src/components/checkbox/Checkbox';
import { addDays, formatDate } from '@src/utils/time';
import { Switch } from 'antd';
import { kebabCase } from 'change-case';

export const validationErrors = {
  name: '',
  uniqueName: '',
  duration: '',
  skill: '',
  overviewAnswers2: '',
  comingSoonAt: '',
  competency: '',
  lessonDetails: '',
};

export type ValidationErrorKeys = keyof typeof validationErrors;

type Props = {
  lesson: EditSllLesson;
  onChange: (p: Partial<EditSllLesson>) => void;
  setLoading: (state: boolean) => void;
  lessonlist:  SelectOption[];
  lessonLoading: boolean;
  getUrl: LazyQueryExecFunction<GetUrlQuery, Exact<{input: VideoUploadInput}>>
  fileUploadingPercent: number;
  setFileUploadingPercent: (percent: number) => void;
  setErrors: (e: Partial<Record<ValidationErrorKeys, any>>) => void;
  errors: Partial<Record<ValidationErrorKeys, any>>;
};

export const options = [
  {label: "Australia", value: "Australia"},
  {label: "Canada", value: "Canada"},
  {label: "USA", value: "USA"},
  {label: "Canada & USA", value: "Canada & USA"},
  {label: "All Countries", value: "All Countries"}
]

export const lessonOptions = [
  {label: "Primary", value: "0"},
  {label: "Additional", value: "1"}
]

export const typeOptions = [
  {label: "Student Voice", value: "Normal"},
  {label: "Student Introduction", value: "Getting Started"},
  {label: "Caregiver", value: "Caregiver"},
  {label: "Brain Breaks/Skill-Builders", value: "Wellbeing Foundation"},
  {label: "Transitions", value: "Transitions"},
  {label: "High School", value: 'High School'},
  {label: "Onboarding", value: 'Onboarding'},
]

export const coverOptions = [
  {label: "Old Cover", value: "old"},
  {label: "New Cover", value: "new"}
]

export const labelOptions = [
  {label: "Empty", value: "Empty"},
  {label: "New", value: "New"},
  {label: "Coming Soon", value: "Coming Soon"},
  {label: "Updated", value: "Updated"}
]

export const formatOptions = [
  {label: "Lesson", value: "Lesson"},
  {label: "Exercise", value: "Exercise"},
]

export const grad1ThemeOptions = [
  {label: "Feeling", value: "Feeling"},
  {label: "Making Choices", value: "Making Choices"},
  {label: "Friendship", value: "Friendship"},
  {label: "Kindness", value: "Kindness"},
]

function EditLessonForm(props: Props) {
  const { onChange, lesson, errors, setErrors, lessonlist, lessonLoading } = props;

  const mainTopics = useMemo(() => JSON.parse(lesson?.mainTopics || '[]'), [lesson?.mainTopics]);
  // const subTopics = useMemo(() => JSON.parse(lesson?.subTopics || '[]'), [lesson?.subTopics]);
  const priorLessons = useMemo(() => JSON.parse(lesson?.recommendedPrior || '[]'), [lesson?.recommendedPrior]);
  const nextLessons = useMemo(() => JSON.parse(lesson?.recommendedNext || '[]'), [lesson?.recommendedNext]);

  const lessonObjectsHeading = useMemo(() => lesson?.overviewHeading2 || 'This lesson will teach:', [lesson?.overviewHeading2]);

  const lessonObjectsAnswers = useMemo(() => JSON.parse(lesson?.overviewAnswers2 || '[]'), [lesson?.overviewAnswers2]);

  const groups = useQueryDictionary({ dictionary: 'ProgramTypes' });
  const groupOptions: SelectOption[] = useMemo(() => [{label: 'All', value: 'all'}, ...groups], [groups]);

  const competency = useMemo(() => JSON.parse(lesson?.competency || '[]'), [lesson?.competency])
  const categories = useMemo(() => JSON.parse(lesson?.categories || '[]'), [lesson?.categories])
  // const themes = useMemo(() => JSON.parse(lesson?.mainTopics || '[]'), [lesson?.mainTopics])

  // This is same field as AgeGroup for AUS
  const gradeLevel = useMemo(() => {
    try {
      return JSON.parse(lesson.gradeLevel ?? '[]') as string[]
    } catch (err) {
      return []
    }
  }, [lesson?.gradeLevel])

  const ageGroup = useMemo(() => {
    try {
      return JSON.parse(lesson.ageGroup ?? '[]') as string[]
    } catch (err) {
      return []
    }
  }, [lesson?.ageGroup])

  // const themeOptionsForGrades = useMemo(() => {
  //   let themes: SelectOption[] = []
  //   gradeLevel.forEach(key => {
  //     if (themeOptions[key] && themeOptions[key].length > 0) {
  //       themeOptions[key].forEach((opt: SelectOption) => {
  //         if (themes.findIndex(th => th.value === opt.value) === -1) {
  //           themes.push(opt)
  //         }
  //       })
  //     }
  //   })
  //   return themes
  // }, [gradeLevel])

  const [comingSoonAt, setComingSoonAt] = useState<string>('');
  const [lessonType, setLessonType] = useState("0");
  const [label, setLabel] = useState('');
  const [cover, setCover] = useState(coverOptions[0].value)

  const [skill, setSkill] = useState('')
  const [mood, setMood] = useState('')
  const [direction, setDirection] = useState('')
  const [targetArea, setTargetArea] = useState('')

  const [tiers, setTiers] = useState('')
  const [containSlides, setContainSlides] = useState('')
  const [containVideo, setContainVideo] = useState('')
  const [containAudio, setContainAudio] = useState('')

  useEffect(() => {
    if(lesson?.lessonType){
      setLessonType(lesson.lessonType)
    }
  }, [lesson.lessonType])
  useEffect(() => {
    if (lesson.isNew) {
      setLabel(labelOptions[1].value)
    } else if (lesson.isComingSoon) {
      setLabel(labelOptions[2].value)
    } else if (lesson.isUpdated) {
      setLabel(labelOptions[3].value)
    } else {
      setLabel(labelOptions[0].value)
    }
  }, [lesson])

  useEffect(() => {
    if (lesson.isNewCover) {
      setCover(coverOptions[1].value)
    } else {
      setCover(coverOptions[0].value)
    }
  }, [lesson.isNewCover])

  useEffect(() => {

    if (categories['Skill']) {
      setSkill(categories['Skill'])
    }
    if (categories['Mood']) {
      setMood(categories['Mood'])
    }
    if (categories['Tiers']) {
      // TO DO: currently, we have bad datas for the Tiers in the DB. We have to udpate the Tiers manually at the edit-lesson
      // After DB update, we should revert to the prev version that is commented out.
      const tiersOption = categoryTiersOptions.find(o => (`${o.value}` === `${categories['Tiers']}` || o.oldValues?.includes(categories['Tiers'])))
      setTiers(tiersOption?.value || '')
      // setTiers(categories['Tiers'])
    }
    if (categories['Direction']) {
      setDirection(categories['Direction'])
    }
    if (categories['Target Area']) {
      setTargetArea(categories['Target Area'])
    }
    if (categories['Direction']) {
      setDirection(categories['Direction'])
    }
    if (categories['ContainSlides']) {
      setContainSlides(categories['ContainSlides'])
    }
    if (categories['ContainVideo']) {
      setContainVideo(categories['ContainVideo'])
    }
    if (categories['ContainAudio']) {
      setContainAudio(categories['ContainAudio'])
    }

  }, [categories])

  useEffect(() => {
    if (lesson?.comingSoonAt) {
      let d;
      if (lesson.comingSoonAt.includes('-')) {
        d = new Date(lesson.comingSoonAt);
      } else {
        d = new Date(parseInt(lesson.comingSoonAt));
      }
      setComingSoonAt(formatDate(d.valueOf(), 'yyyy-MM-dd'));
    }
  }, [lesson.comingSoonAt]);

  const today = formatDate(addDays(new Date(), 1).valueOf(), 'yyyy-MM-dd');

  const handleChangeText = useCallback((key: keyof typeof validationErrors) => (value: TextInputValue) => {
    setErrors({
      ...errors,
      [key]: '',
    });
    if (key === 'uniqueName') {
      value = value.toString().replace(/\s+/g, '-').replace(/[^A-Za-z-0-9-]/g, '').toLowerCase();
    }
    onChange({[key]: value});
  }, [errors, onChange, setErrors]);

  const handleChangeLessonObjectivesHeading = useCallback((value: TextInputValue) => {
    onChange({ overviewHeading2: value as string });
  }, [onChange]);

  const handleLessonObjectivesAnswersRemoveOption = useCallback((id: number) => {
    const curr = [...lessonObjectsAnswers];
    onChange({ overviewAnswers2: JSON.stringify(
      curr.filter((child, index) => {
        return index !== id
      })
    ) });
  }, [lessonObjectsAnswers, onChange]);

  const handleLessonObjectivesAnswersChangeOption = useCallback((index: number, value: TextInputValue) => {
    const answers = [...lessonObjectsAnswers];
    answers[index] = value.toString()
    onChange({ overviewAnswers2: JSON.stringify(answers) });
  }, [lessonObjectsAnswers, onChange]);

  const handleAddLessonObjectivesAnswers = useCallback(() => {
    const curr = [...lessonObjectsAnswers];
    curr.push('');
    onChange({ overviewAnswers2: JSON.stringify(curr) });
  }, [lessonObjectsAnswers, onChange]);

  const handleChangePriorLessons = useCallback((value: string[]) => {
    onChange({ recommendedPrior: JSON.stringify(value) });
  }, [onChange]);

  const handleChangeNextLessons = useCallback((value: string[]) => {
    onChange({ recommendedNext: JSON.stringify(value) });
  }, [onChange]);

  const handleChangeSkill = useCallback((v) => {
    onChange({ competency: v?.length ? JSON.stringify(v) : '' });
  }, [onChange]);

  const handleChangeCountry = useCallback((v: string) => {
    onChange({ country: v });
  }, [onChange]);

  const handleChangeCoverType = useCallback((v: string) => {
    onChange({ isNewCover: v === coverOptions[1].value });
  }, [onChange]);

  const handleGradeLevel = useCallback((v: string[]) => {
    onChange({ gradeLevel: v.length ? JSON.stringify(v) : '', ageGroup: undefined });

    let themes: string[] = []
    v.forEach(key => {
      if (themeOptions[key] && themeOptions[key].length > 0) {
        themeOptions[key].forEach((v: SelectOption) => {
          if (!themes.includes(v.value)) {
            themes.push(v.value)
          }
        })
      }
    })
    if (themes.length) {
      onChange({ mainTopics: themes.length ? JSON.stringify(themes) : '' });
    }
  }, [onChange]);

  const handleAgeGroup = useCallback((v: SelectOption | string[]) => {
    if (Array.isArray(v)) {
      onChange({ ageGroup: v.length ? JSON.stringify(v) : '', gradeLevel: undefined });

      let themes: string[] = []
      v.forEach(key => {
        if (themeOptions[key] && themeOptions[key].length > 0) {
          themeOptions[key].forEach((v: SelectOption) => {
            if (!themes.includes(v.value)) {
              themes.push(v.value)
            }
          })
        }
      })
      if (themes.length) {
        onChange({ mainTopics: themes.length ? JSON.stringify(themes) : '' });
      }
    } else {
      onChange({ ageGroup: v.value, gradeLevel: undefined});

      if (themeOptions[v.value] && themeOptions[v.value].length > 0) {
        const themes = (themeOptions[v.value] ?? []).map(o => o.value)
        onChange({ mainTopics: themes?.length ? JSON.stringify(themes) : '' });
      }
    }
  }, [onChange]);

  const handleChangeImage = useCallback((image: string, file: File | undefined) => {
    onChange({ coverImage: file });
  }, [onChange]);

  const onChangeMainTopics = useCallback((value: string[]) => {
    onChange({ mainTopics: JSON.stringify(value) });
  }, [onChange]);

  const handleChangeIsPublished = useCallback((v: boolean) => {
    onChange({ isPublished: v });
  }, [onChange]);

  const handleChangeGroup = useCallback((v) => {
    onChange({ groupId: (!v?.value || `${v.value}` === 'all') ? null : v.value });
  }, [onChange]);

  const handleChangeIsNew = useCallback((v: boolean) => {
    onChange({ isNew: v });
  }, [onChange]);

  const handleChangeIsComingSoon = useCallback((v: boolean) => {
    onChange({ isComingSoon: v });
  }, [onChange]);

  const handleChangeIsUpdated = useCallback((v: boolean) => {
    onChange({ isUpdated: v });
  }, [onChange]);

  const handleChangeComingSoonAt = useCallback((v) => {
    setErrors({
      ...errors,
      comingSoonAt: ''
    });
    onChange({'comingSoonAt': v});
    // setComingSoonAt(v);
  }, [errors, onChange, setErrors]);

  const handleChangeComingSoonLabelPosition = useCallback((v: string) => {
    onChange({ comingSoonLabelPosition: v });
  }, [onChange]);

  const handleChangeUpdatedLabelPosition = useCallback((v: string) => {
    onChange({ updatedLabelPosition: v });
  }, [onChange]);

  const handleChangeNewLabelPosition = useCallback((v: string) => {
    onChange({ newLabelPosition: v });
  }, [onChange]);

  const handleLessonType = (value: string) => {
    onChange({ lessonType: value  })
    setLessonType(value)
  }

  const handleChangeLabel = useCallback((v: string) => {
    setLabel(v);
    handleChangeIsNew(v === labelOptions[1].value)
    handleChangeIsComingSoon(v === labelOptions[2].value)
    handleChangeIsUpdated(v === labelOptions[3].value)

  }, [setLabel, handleChangeIsNew, handleChangeIsComingSoon, handleChangeIsUpdated]);

  const handleChangeIsRequirement = useCallback(() => {
    onChange({showLessonMaterialsNotice: !lesson.showLessonMaterialsNotice})
  }, [onChange, lesson.showLessonMaterialsNotice]);

  const handleChangeCategories = useCallback((key: any) => (option: any) => {
    let updateCategories = categories;
    if (updateCategories[key]) {
      updateCategories[key] = option.value.toString()
    } else {
      updateCategories = { ...categories, [key]: option.value.toString()}
    }
    onChange({ categories: JSON.stringify(updateCategories) });
  }, [categories, onChange]);

  const handleChangeDuration = useCallback((value: TextInputValue) => {
    onChange({suggestedDuration: value.toString()});
  }, [onChange]);

  const handleExtension = useCallback((v: boolean) => {
    onChange({isExtensionAvailable: v})
  }, [onChange])

  // const handleChangeTheme = useCallback((v: any) => {
  //   logger.info('Topics : ', v)
  //   onChange({ mainTopics: v?.length ? JSON.stringify(v) : '' });
  // }, [onChange]);

  const handleChangeNewLayout = useCallback((v: boolean) => {
    onChange({ isEducatorCaregiverLessonLayout: v });
  }, [onChange]);

  const handleChangeContainSlides = useCallback((v: boolean) => {
    let updatedCategories = { ...categories };
    if (v) {
      updatedCategories["ContainSlides"] = "0";
      setContainSlides("0")
    } else {
      delete updatedCategories["ContainSlides"];
      setContainSlides("")
    }
    onChange({ categories: JSON.stringify(updatedCategories) });
  }, [categories, onChange]);

  const handleChangeContainVideo = useCallback((v: boolean) => {
    let updatedCategories = { ...categories };
    if (v) {
      updatedCategories["ContainVideo"] = "1";
      setContainVideo("1")
    } else {
      delete updatedCategories["ContainVideo"];
      setContainVideo("")
    }
    onChange({ categories: JSON.stringify(updatedCategories) });
  }, [categories, onChange]);

  const handleChangeContainAudio = useCallback((v: boolean) => {
    let updatedCategories = { ...categories };
    if (v) {
      updatedCategories["ContainAudio"] = "2";
      setContainAudio("2")
    } else {
      delete updatedCategories["ContainAudio"];
      setContainAudio("")
    }

    onChange({ categories: JSON.stringify(updatedCategories) });
  }, [categories, onChange]);

  return (
    <>
      <div className={styles.row} style={{ marginTop: '40px'}}>
        <div className={`${styles.uploadertitle}`}>Name</div>
        <TextInput
          hasError={!!errors?.name}
          subTitle={errors?.name}
          label={''}
          id={'name'}
          placeholder={'Add lesson name'}
          value={lesson.name}
          onChange={handleChangeText('name')}
        />
      </div>

      <div className={styles.row} style={{ marginTop: '15px'}}>
        <TextInput
          hasError={!!errors?.uniqueName}
          subTitle={errors?.uniqueName}
          label={() => <div>Pretty URL <span style={{color: 'gray'}}>(If left empty, defaults to Lesson Name)</span></div>}
          id={'uniqueName'}
          placeholder={'Add pretty URL'}
          value={lesson?.uniqueName || ''}
          onChange={handleChangeText('uniqueName')}
        />
        <div>
          <span style={{color: 'gray'}}>{`Sample: ${window.location.protocol}//${window.location.host}/standalone/`}</span>
          <span>{kebabCase(lesson?.uniqueName || lesson.name)}</span>
        </div>
      </div>

      <div className={`${styles.newBox} ${styles.row}`}>
        <div className={`${styles.subBoxTitle}`}>Publish/Draft</div>
        <div className={styles.row}>
          <Checkbox id={'isPublished'} checked={!!lesson.isPublished} onChange={handleChangeIsPublished} label={'Publish this lesson'}/>
        </div>
        <div className={styles.row}>
          <div className={styles.subBoxTitle}>Lesson Type</div>
          <RadioGroup
            value={lessonType}
            options={lessonTypeOptionsWithNone}
            onChange={handleLessonType}
            name={''}
            hasError={false}
            subTitle={""}
          />
        </div>

        <div className={styles.subBoxTitle}>Labels</div>
          <RadioGroup
            value={label}
            options={labelOptions}
            onChange={handleChangeLabel}
            name={''}
            hasError={false}
            subTitle={""}
          />
          {
            lesson.isComingSoon ?
              <>
                <div className={`${styles.comingSoonAtTitle}`}>Coming Soon At:</div>
                <TextInput
                  hasError={!!errors.comingSoonAt}
                  subTitle={errors.comingSoonAt}
                  className={styles.comingSoonAt}
                  label={''}
                  id={'comingSoonAt'}
                  value={comingSoonAt}
                  onChange={handleChangeComingSoonAt}
                  type={'date'}
                  min={today}
                />
                <div className={styles.comingSoonAt}>
                  <div className={styles.topicTitle}>Coming Soon label Position</div>
                  <RadioGroup
                    value={lesson.comingSoonLabelPosition || gradeLabelPositionOptions[3].value}
                    options={gradeLabelPositionOptions}
                    onChange={handleChangeComingSoonLabelPosition}
                    name={''}
                    hasError={false}
                    subTitle={""}
                  />
                </div>
              </> : null
          }
          {
            lesson.isNew ?
              <div className={styles.comingSoonAt}>
                <div className={styles.topicTitle}>New label Position</div>
                <RadioGroup
                  value={lesson.newLabelPosition || gradeLabelPositionOptions[3].value}
                  options={gradeLabelPositionOptions}
                  onChange={handleChangeNewLabelPosition}
                  name={''}
                  hasError={false}
                  subTitle={""}
                />
              </div> : null
          }
          {
            lesson.isUpdated ?
            <div className={styles.comingSoonAt}>
              <div className={styles.topicTitle}>Updated label Position</div>
              <RadioGroup
                value={lesson.updatedLabelPosition || gradeLabelPositionOptions[3].value}
                options={gradeLabelPositionOptions}
                onChange={handleChangeUpdatedLabelPosition}
                name={''}
                hasError={false}
                subTitle={""}
              />
            </div> : null
          }
        </div>

        <div className={styles.row}>
          <div className={styles.videoUploadTitle}>Lesson Country</div>
          <RadioGroup
            value={lesson.country || 'All Countries'}
            options={options}
            onChange={handleChangeCountry}
            name={''}
            hasError={false}
            subTitle={""}
          />
        </div>
        {
          <div className={styles.row}>
            <Select
              renderLabel={() => <div className={`${styles.subBoxTitle} ${styles.row}`}>Group</div>}
              label={'Group'}
              options={groupOptions}
              value={lesson.groupId || 'all'}
              onChange={handleChangeGroup}
            />
          </div>
        }
        {/* For Australia only */}
        {lesson.country === options[0].value &&
          <div className={`${styles.overview} ${styles.row}`}>
            <div className={styles.categoryView}>
              <div className={styles.opReelBox}>
                <div className={styles.opReelSubTitle}>Key Info</div>
                <div className={styles.keyInfoSubBox} style={{marginBottom: '24px'}}>
                  {(
                    <div className={styles.categoryRowBox}>
                      <div className={styles.categoryBox}>
                        <SelectMulti
                          label={'Age Group'}
                          options={AustraliaStandaloneLessonAgeGroupOptions}
                          value={ageGroup}
                          onChange={handleAgeGroup}
                          doNotSort
                        />
                      </div>
                      <div className={styles.categoryBox}>
                        <TextInput
                          label={'Suggested Duration (mins)'}
                          id={'durations'}
                          value={lesson.suggestedDuration ?? ''}
                          onChange={handleChangeDuration}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {(lessonType !== "5" && lessonType !== "0") && <>
                <div className={styles.opReelSubTitle}>Categories</div>
                {((lessonType === "1" || lessonType === "2" || lessonType === "3" || lessonType === "6")) && (
                    <div className={styles.categoryContainerBox}>
                      <div className={styles.categoryTupleSubBox}>
                        <div className={styles.leftHalfBox}>
                          <Select
                            label={'Skill'}
                            showSearch={false}
                            options={categorySkillsOptions}
                            value={skill} onChange={handleChangeCategories('Skill')}/>
                        </div>
                        <div className={styles.rightHalfBox}>
                          <SelectMulti
                            error={!!errors.competency}
                            subTitle={errors?.competency}
                            label={'Competency'}
                            options={standaloneLessonsSelCompetencies}
                            onChange={handleChangeSkill}
                            value={competency || standaloneLessonsSelCompetencies[0].value}
                          />
                        </div>
                        <div className={styles.leftHalfBox}>
                          <Select
                            label={'Mood'}
                            showSearch={false}
                            options={categoryMoodOptions1}
                            value={mood}
                            onChange={handleChangeCategories('Mood')}
                          />
                        </div>
                      </div>
                      <div style={{display: 'flex', gap: 50}}>
                        <div className={styles.categoryTechBox}>
                          <div className={styles.opReelSubTitle}>Technology</div>
                          <div className={styles.techRowBox}>
                            <div className={styles.row}>
                              <Checkbox
                                id={'slides'}
                                checked={containSlides === "0"}
                                onChange={handleChangeContainSlides}
                                label={'Contains Slides'}
                              />
                            </div>
                            <div className={styles.row} style={{marginBottom: 24}}>
                              <Checkbox
                                id={'videos'}
                                checked={containVideo === "1"}
                                onChange={handleChangeContainVideo}
                                label={'Contains Video'}
                              />
                            </div>
                            <div className={styles.row}>
                              <Checkbox
                                id={'audios'}
                                checked={containAudio === "2"}
                                onChange={handleChangeContainAudio}
                                label={'Contains Audio'}
                              />
                            </div>
                          </div>
                        </div>
                        {lessonType !== "6" && <div className={styles.categoryTechBox}>
                          <div className={styles.opReelSubTitle}>Extension</div>
                          <div className={styles.techRowBox}>
                            <div className={styles.row}>
                              <Checkbox
                                id={'extension'}
                                checked={!!lesson.isExtensionAvailable}
                                onChange={handleExtension}
                                label={'Extension Available'}
                              />
                            </div>
                          </div>
                        </div>}
                      </div>
                    </div>
                  )}
                  {lessonType === "4" && (
                    <div className={styles.keyInfoSubBox} style={{gap: 30}}>
                      <div className={styles.categoryBox}>
                        <TextInput
                          label={''}
                          id={'lessondetails'}
                          placeholder={'Input lesson details'}
                          value={lesson.lessonDetails || ''}
                          onChange={handleChangeText('lessonDetails')}
                        />
                      </div>
                    </div>
                  )}
                  {lessonType === "7" && (
                    <div className={styles.categorySubBox}>
                      <div className={styles.categoryBox}>
                        <Select
                          label={'Direction'}
                          showSearch={false}
                          options={categoryDirectionOptions}
                          value={direction}
                          onChange={handleChangeCategories('Direction')}
                        />
                      </div>
                      <div className={styles.categoryBox}>
                        <Select
                          label={'Target Area'}
                          showSearch={false}
                          options={categoryTargetAreaOptions}
                          value={targetArea}
                          onChange={handleChangeCategories('Target Area')}
                        />
                      </div>
                    </div>
                  )}
                </>}
              </div>
            </div>
          </div>
        }

        {/* For North America only, USA & Canada */}
        {(lesson.country === options[1].value || lesson.country === options[2].value || lesson.country === options[3].value || lesson.country === options[4].value)
          ? (
            <div className={`${styles.overview}  ${styles.row}`}>
              <div className={styles.categoryView}>
                <div className={styles.opReelBox}>
                <div className={styles.opReelSubTitle}>Key Info</div>
                  <div className={styles.categorySubBox} style={{marginBottom: '24px'}}>
                      <div className={styles.leftHalfBox}>
                        <SelectMulti
                          label={'Age Group'}
                          options={NorthAmericaStandaloneLessonAgeGroupOptions}
                          value={gradeLevel}
                          onChange={handleGradeLevel}
                          doNotSort
                        />
                      </div>
                      <div className={styles.rightHalfBox}>
                        <TextInput
                          label={'Duration'}
                          id={'duration'}
                          placeholder={'Duration'}
                          value={lesson.duration || ''}
                          onChange={handleChangeText('duration')}
                        />
                      </div>
                  </div>
                  {lessonType !== "5" && <>
                  <div className={styles.opReelSubTitle}>Categories</div>
                  {((lessonType === "0" || lessonType === "1" || lessonType === "2" || lessonType === "3" || lessonType === "6")) && (
                    <div className={styles.categoryContainerBox}>
                    <div className={styles.categoryTupleSubBox}>
                        <div className={styles.leftHalfBox}>
                          <Select
                            label={'Tiers'}
                            showSearch={false}
                            options={categoryTiersOptions}
                            value={tiers} onChange={handleChangeCategories('Tiers')}/>
                        </div>
                        <div className={styles.rightHalfBox}>
                          <SelectMulti
                            error={!!errors.competency}
                            subTitle={errors?.competency}
                            label={'Competency'}
                            options={standaloneLessonsSelCompetencies}
                            onChange={handleChangeSkill}
                            value={competency || standaloneLessonsSelCompetencies[0].value}
                          />
                        </div>
                        <div className={styles.leftHalfBox}>
                          <Select
                            label={'Mood'}
                            showSearch={false}
                            options={categoryMoodOptions1}
                            value={mood}
                            onChange={handleChangeCategories('Mood')}
                          />
                        </div>
                    </div>
                    <div style={{display: 'flex', gap: 50}}>
                      <div className={styles.categoryTechBox}>
                        <div className={styles.opReelSubTitle}>Technology</div>
                        <div className={styles.techRowBox}>
                          <div className={styles.row}>
                            <Checkbox
                              id={'slides'}
                              checked={containSlides === "0"}
                              onChange={handleChangeContainSlides}
                              label={'Contains Slides'}
                            />
                          </div>
                          <div className={styles.row} style={{marginBottom: 24}}>
                            <Checkbox
                              id={'videos'}
                              checked={containVideo === "1"}
                              onChange={handleChangeContainVideo}
                              label={'Contains Video'}
                            />
                          </div>
                          <div className={styles.row}>
                            <Checkbox
                              id={'audios'}
                              checked={containAudio === "2"}
                              onChange={handleChangeContainAudio}
                              label={'Contains Audio'}
                            />
                          </div>
                        </div>
                      </div>
                      {lessonType !== "6" && <div className={styles.categoryTechBox}>
                        <div className={styles.opReelSubTitle}>Extension</div>
                        <div className={styles.techRowBox}>
                          <div className={styles.row}>
                            <Checkbox
                              id={'extension'}
                              checked={!!lesson.isExtensionAvailable}
                              onChange={handleExtension}
                              label={'Extension Available'}
                            />
                          </div>
                        </div>
                      </div>}
                      </div>
                    </div>
                  )}
                  {lessonType === "4" && (
                    <div className={styles.keyInfoSubBox} style={{gap: 30}}>
                      <div className={styles.categoryBox}>
                        <TextInput
                          label={''}
                          id={'lessondetails'}
                          placeholder={'Input lesson details'}
                          value={lesson.lessonDetails || ''}
                          onChange={handleChangeText('lessonDetails')}
                        />
                      </div>
                    </div>
                  )}
                  {lessonType === "7" && (
                    <div className={styles.categorySubBox}>
                      <div className={styles.categoryBox}>
                        <Select
                          label={'Direction'}
                          showSearch={false}
                          options={categoryDirectionOptions}
                          value={direction}
                          onChange={handleChangeCategories('Direction')}
                        />
                      </div>
                      <div className={styles.categoryBox}>
                        <Select
                          label={'Target Area'}
                          showSearch={false}
                          options={categoryTargetAreaOptions}
                          value={targetArea}
                          onChange={handleChangeCategories('Target Area')}
                        />
                      </div>
                    </div>
                  )}
                  </>}
                </div>
              </div>
            </div>
        ) : null}

        <div className={styles.row}>
          <div className={styles.requirementView} onClick={handleChangeIsRequirement}>
            <Switch checked={!!lesson.showLessonMaterialsNotice} /><span>{'Lesson Materials Notice'}</span>
          </div>
          {lesson.showLessonMaterialsNotice &&
            <TextInput
              label={''}
              id={'requirements'}
              placeholder={''}
              value={'This lesson only contains downloadable materials.'}
              onChange={()=>{}}
            />
          }
        </div>
        <div className={styles.row} style={{marginTop: 20}}>
          <div className={styles.subBoxTitle}>Educator/Caregiver Lesson Layout: </div>
          <Checkbox
            id={'Educator-Caregiver-Lesson-Layout'}
            checked={!!lesson.isEducatorCaregiverLessonLayout}
            onChange={handleChangeNewLayout}
            label={'Educator/Caregiver Lesson Layout'}
          />
        </div>

        <div className={styles.row}>
          <div className={styles.uploadertitle}>Lesson Cover</div>
          <div className={styles.categoryView}>
            <RadioGroup
              value={cover}
              options={coverOptions}
              onChange={handleChangeCoverType}
              name={''}
              hasError={false}
              subTitle={""}
            />
          </div>
          <ImageUploadForm url={lesson.coverImage || undefined} onChange={handleChangeImage}/>
        </div>

        <hr className={styles.horizDivider} />

        <div className={styles.subTitle}>Overview</div>
        <div className={styles.row}>
          <div className={styles.topicTitle}>Add Lesson Tags</div>
          <TagsForm tags={mainTopics} setTags={onChangeMainTopics} />
        </div>

        {/* When country is Australia or All Countries */}
        {/* {(lesson.country === options[0].value || lesson.country === options[4].value) &&
          <>
            <div className={styles.row}>
              <div className={styles.videoUploadTitle}>Competency</div>
              <SelectMulti className={styles.unitCourse}
                inline={false}
                label={''}
                options={standaloneLessonsSelCompetencies}
                onChange={handleChangeSkill}
                value={competency || standaloneLessonsSelCompetencies[0].value}
              />
              {!!errors?.skill && <div className={styles.error}>{errors?.skill}</div>}
            </div>
          </>
        } */}

        <div className={styles.overview}>
          <div className={styles.checkView}>
            <div className={styles.topicTitle}>Lesson Objectives:</div>
            <div className={styles.statements}>
              <AnswersForm
                description={lessonObjectsHeading}
                answers={lessonObjectsAnswers}
                showError={!!errors?.overviewAnswers2}
                onUpdateHeading={handleChangeLessonObjectivesHeading}
                onRemove={handleLessonObjectivesAnswersRemoveOption}
                onChange={handleLessonObjectivesAnswersChangeOption}
                onAdd={handleAddLessonObjectivesAnswers}
                noDescription={true}
              />
            </div>
          </div>
        </div>
        <hr className={styles.horizDivider} />

        <div className={styles.row}>
          <div className={`${styles.subViewTitle} ${styles.row}`}>Recommended Prior Learning</div>
          <SelectMulti
            disabled={lessonlist.length === 0 || lessonLoading}
            label={''}
            options={lessonlist}
            value={priorLessons}
            onChange={handleChangePriorLessons}
          />
        </div>

        <hr className={styles.horizDivider} />

        <div className={styles.row}>
          <div className={`${styles.subViewTitle} ${styles.row}`}>Recommended Next Lessons</div>
          <SelectMulti
            disabled={lessonlist.length === 0 || lessonLoading}
            label={''}
            options={lessonlist}
            value={nextLessons}
            onChange={handleChangeNextLessons}
          />
        </div>
    </>
  );
}

export default EditLessonForm;
